/**
 * Barra sticky de 'Añadir al carrito' del PDP en móvil (#236).
 *
 * Muestra [data-product-sticky-atc] cuando el botón real de add-to-cart
 * (.product-form__add-button) no está a la vista, con una excepción (#472):
 * mientras el bloque de selectores (.product-form__variants) quedaría tapado
 * por la barra (al cargar, en la primera pantalla), la barra no se muestra.
 * Aparece en cuanto ese bloque cabe completo entre el header y la barra, o ya
 * empezó a salir por arriba; y siempre que el botón real ya quedó arriba.
 * Difiere de la barra del carrito (#233), que se muestra sin excepción.
 *
 * El botón de la barra NO es submit: reenvía el clic al botón real del form.
 * Así se reutiliza el flujo actual completo — validación nativa del form,
 * apps que escuchan el clic en el botón original (TiB procesa el diseño ahí
 * y detiene la propagación) y el feedback de 'Añadiendo…' (#220), cuyo
 * listener corre en fase de captura a nivel document y acepta clics
 * programáticos.
 *
 * El estado del botón real (texto, is-adding, disabled) se refleja con un
 * MutationObserver en vez de duplicar la lógica de #220: el spinner y los
 * resets (bfcache/pageshow, fallback de 30s) quedan sincronizados solos.
 *
 * El header es sticky (enable_sticky_header): el CTA queda tapado ANTES de
 * salir del viewport, así que recortamos el root por arriba con rootMargin
 * negativo igual a la altura del header (mismo patrón que
 * extra_js_cart_sticky_bar.js).
 */
(function () {
  'use strict';

  var bar = document.querySelector('[data-product-sticky-atc]');
  var realButton = document.querySelector('.product-form [data-action="add-to-cart"]');

  if (!bar || !realButton || !('IntersectionObserver' in window) || !('MutationObserver' in window)) {
    return;
  }

  var stickyButton = bar.querySelector('[data-sticky-atc-button]');
  var variantLabel = bar.querySelector('[data-sticky-atc-variant]');

  if (!stickyButton) {
    return;
  }

  // --- Mostrar/ocultar la barra ---

  // --header-is-sticky (0/1) y --header-height (px) los mantiene el tema en
  // <html>: el header component recalcula la altura en resize (theme.js).
  function headerOffset() {
    var styles = getComputedStyle(document.documentElement);
    var isSticky = parseInt(styles.getPropertyValue('--header-is-sticky'), 10) || 0;
    var height = parseInt(styles.getPropertyValue('--header-height'), 10) || 0;
    return isSticky * height;
  }

  var variants = document.querySelector('.product-form__variants');
  var buttonVisible = false;
  var buttonPassed = false;
  var selectorsClear = !variants;

  function render() {
    var showBar = !buttonVisible && (buttonPassed || selectorsClear);
    bar.classList.toggle('is-visible', showBar);
    // Igual que en #233: el launcher de Zoko (fixed, bottom:10px, z-index
    // 9999999) taparía la barra; esta clase en <body> lo sube via CSS y
    // además reserva espacio al final del footer para que la barra no
    // tape el último contenido de la página.
    document.body.classList.toggle('pdp-sticky-atc-visible', showBar);
  }

  var observer = null;
  var variantsObserver = null;

  function initObserver() {
    if (observer) {
      observer.disconnect();
    }
    if (variantsObserver) {
      variantsObserver.disconnect();
    }

    var top = headerOffset();

    observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        // rootBounds ya viene recortado por el rootMargin del header: el botón
        // "pasó" cuando su borde superior queda por encima de ese límite.
        var rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
        buttonVisible = entry.isIntersecting;
        buttonPassed = !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
        render();
      },
      { rootMargin: '-' + top + 'px 0px 0px 0px' }
    );
    observer.observe(realButton);

    if (variants) {
      // Root recortado también por abajo con el alto de la barra: ratio 1 = el
      // bloque de selectores cabe completo entre el header y la barra.
      variantsObserver = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          selectorsClear = entry.intersectionRatio >= 0.99 || entry.boundingClientRect.top < rootTop;
          render();
        },
        { rootMargin: '-' + top + 'px 0px -' + bar.offsetHeight + 'px 0px', threshold: [0, 1] }
      );
      variantsObserver.observe(variants);
    }
  }

  initObserver();

  // theme.js fija --header-height al inicializar el header; si este script
  // corre antes, el primer observer nace con offset 0. Reconstruimos en load
  // (ya con la variable puesta) y en resize (la altura del header cambia).
  window.addEventListener('load', initObserver);

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initObserver, 250);
  });

  // --- Clic: reenviar al botón real ---

  stickyButton.addEventListener('click', function () {
    if (stickyButton.disabled || realButton.disabled || realButton.classList.contains('is-adding')) {
      return;
    }
    realButton.click();
  });

  // --- Espejo del estado del botón real ---

  function syncFromRealButton() {
    stickyButton.innerHTML = realButton.innerHTML;
    stickyButton.classList.toggle('is-adding', realButton.classList.contains('is-adding'));
    stickyButton.disabled = realButton.disabled;

    if (realButton.getAttribute('aria-busy') === 'true') {
      stickyButton.setAttribute('aria-busy', 'true');
    } else {
      stickyButton.removeAttribute('aria-busy');
    }
  }

  // Las mutaciones se entregan en microtask, después de que #220 termina de
  // cambiar clase + innerHTML del botón real: al sincronizar aquí ya leemos
  // el estado final.
  new MutationObserver(syncFromRealButton).observe(realButton, {
    attributes: true,
    attributeFilter: ['class', 'disabled', 'aria-busy'],
    childList: true,
    subtree: true,
    characterData: true
  });

  syncFromRealButton();

  // --- Precio y variante seleccionada ---
  // El precio se actualiza solo: los spans de la barra llevan las clases
  // js-selected-price / js-precio-normal / priceProducts__list-price-container
  // que extra_js_product_variant_handler.js actualiza con querySelectorAll.
  // La variante sí hay que escucharla (el evento burbujea desde la sección).
  document.addEventListener('variant:changed', function (event) {
    if (!variantLabel || !event.detail || !event.detail.variant) {
      return;
    }
    variantLabel.textContent = event.detail.variant.title;
  });
})();
